import React, { useState, useEffect } from 'react';
import { ErrorBoundary } from '@/app/components/ErrorBoundary';
import { ModeSelector } from '@/app/components/ModeSelector';
import { IsoBlockEditor } from '@/app/components/IsoBlockEditor';
import { BlockSystemEditor } from '@/app/components/BlockSystemEditor';
import { SystemTableEditor } from '@/app/components/SystemTableEditor';
import { DevModeBanner } from '@/app/components/DevModeBanner';
import { AuthHeader } from '@/app/components/AuthHeader';
import { LandingPage } from '@/app/components/LandingPage';
import { MobileRestrictionMessage } from '@/app/components/MobileRestrictionMessage';
import { AppMode, SystemData, PlacedBlock, SocketConnection } from '@/app/types/cad';
import { Toaster } from '@/app/components/ui/sonner';
import { AppModeProvider } from '@/app/providers/AppModeProvider';
import { useAuth, AuthProvider } from '@/app/providers/AuthProvider';
import { SentryStatusBanner } from '@/app/components/SentryStatusBanner';
import { LanguageProvider } from '@/app/contexts/LanguageContext';
import { Impressum } from '@/app/components/legal/Impressum';
import { Datenschutz } from '@/app/components/legal/Datenschutz';
import { AGB } from '@/app/components/legal/AGB';

import { PlanModalProvider, usePlanModal } from '@/app/contexts/PlanModalContext';
import { PlanSelectionModal } from '@/app/components/PlanSelectionModal';

// Pre-import Tabs to help Vite with dynamic loading
import '@radix-ui/react-tabs';

// Load feedback test helpers in development
if (import.meta.env.DEV) {
  import('@/app/utils/feedbackTestHelpers');
}

// LocalStorage keys
const STORAGE_KEYS = {
  systems: 'cad_systems',
  blocks: 'cad_blocks',
  connections: 'cad_connections',
  projectData: 'cad_projectData',
  currentMode: 'cad_currentMode',
};

function IsoBlockApp() {
  const { isAuthenticated, has } = useAuth();
  const { openModal } = usePlanModal();
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [publicRoute, setPublicRoute] = useState<'landing' | 'impressum' | 'privacy' | 'terms'>('landing');

  // Load initial state from localStorage
  const [currentMode, setCurrentMode] = useState<AppMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.currentMode);
    return (saved as AppMode) || 'iso-block';
  });
  
  const [systems, setSystems] = useState<SystemData[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.systems);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [blocks, setBlocks] = useState<PlacedBlock[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.blocks);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [connections, setConnections] = useState<SocketConnection[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.connections);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [projectData, setProjectData] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.projectData);
    return saved ? JSON.parse(saved) : {
      name: 'Mein Projekt',
      customAttributes: {},
    };
  });

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.currentMode, currentMode);
  }, [currentMode]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.systems, JSON.stringify(systems));
  }, [systems]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.blocks, JSON.stringify(blocks));
  }, [blocks]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.connections, JSON.stringify(connections));
  }, [connections]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.projectData, JSON.stringify(projectData));
  }, [projectData]);

  // Check if we need to show the plan modal on startup for free users
  useEffect(() => {
    // Only run if authenticated
    if (isAuthenticated && has) {
       // Check if user is on free plan (and NOT on basic/enterprise just to be safe, though precedence is handled by Clerk)
       // Actually 'has' checks for roles/plans.
       const isFreeUser = has({ plan: 'free_user' });
       // Logic: If user is free, and HAS NOT acknowledged the modal
       if (isFreeUser) {
         const acknowledged = localStorage.getItem('iso_free_plan_acknowledged') === 'true';
         if (!acknowledged) {
           // Small delay to ensure UI is ready and transitions are smooth
           const timer = setTimeout(() => openModal(), 800);
           return () => clearTimeout(timer);
         }
       }
    }
  }, [isAuthenticated, has, openModal]);

  const handleUpdateSystem = (systemId: string, updates: Partial<SystemData>) => {
    setSystems(prev => prev.map(sys => 
      sys.id === systemId ? { ...sys, ...updates } : sys
    ));
  };

  const handleUpdateProjectData = (updates: any) => {
    setProjectData(prev => ({ ...prev, ...updates }));
  };

  // If not authenticated OR explicitly showing landing page
  if (!isAuthenticated || showLandingPage) {
    if (publicRoute === 'impressum') return <Impressum onBack={() => setPublicRoute('landing')} />;
    if (publicRoute === 'privacy') return <Datenschutz onBack={() => setPublicRoute('landing')} />;
    if (publicRoute === 'terms') return <AGB onBack={() => setPublicRoute('landing')} />;
    
    return <LandingPage 
      onEnterApp={() => setShowLandingPage(false)} 
      onNavigate={setPublicRoute}
    />;
  }

  // If authenticated (or in dev mode), show the full App
  // Note: We removed the blocking Protect component because free users are allowed,
  // but they get a popup modal instead.
  return (
    <ErrorBoundary>
      <DevModeBanner />
      <PlanSelectionModal />
      <AppModeProvider mode={currentMode}>
        {/* Mobile Restriction Warning - Visible only on small screens (< md) */}
        <div className="md:hidden h-screen bg-background">
          <MobileRestrictionMessage onShowLandingPage={() => setShowLandingPage(true)} />
        </div>

        {/* Main App - Hidden on small screens */}
        <div className="hidden md:flex flex-col h-screen overflow-hidden" style={{ backgroundColor: 'var(--md-background)' }}>
          <AuthHeader onShowLandingPage={() => setShowLandingPage(true)} />
          <Toaster position="top-right" />
          
          {/* Mode Selector Tabs */}
          <ModeSelector currentMode={currentMode} onModeChange={setCurrentMode} />

          {/* Render current editor based on mode */}
          {currentMode === 'iso-block' && <IsoBlockEditor />}
          {currentMode === 'block-system' && (
            <BlockSystemEditor 
              onSystemsChange={setSystems}
              onBlocksChange={setBlocks}
              onConnectionsChange={setConnections}
            />
          )}
          {currentMode === 'system-table' && (
            <SystemTableEditor
              systems={systems}
              onUpdateSystem={handleUpdateSystem}
              projectData={projectData}
              onUpdateProjectData={handleUpdateProjectData}
              blocks={blocks}
              connections={connections}
              onBlocksChange={setBlocks}
              onConnectionsChange={setConnections}
            />
          )}
        </div>
      </AppModeProvider>
    </ErrorBoundary>
  );
}

// Wrapper component that ensures AuthProvider is available
function App() {
  return (
    <LanguageProvider>
      <SentryStatusBanner />
      <AuthProvider>
        <PlanModalProvider>
          <IsoBlockApp />
        </PlanModalProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
